import { CHIME, HALL_MOOD, LILT, LOOP, ORDER, PHRASE, SECTION_MIX, SONGS } from "./songs";
import { perf } from "./perf";

const PIANO = ["A1", "C2", "Ds2", "Fs2", "A2", "C3", "Ds3", "Fs3", "A3", "C4", "Ds4", "Fs4", "A4", "C5", "Ds5", "Fs5", "A5", "C6"];
const CELLO = ["C2", "E2", "G2", "C3", "E3", "G3", "C4", "E4", "G4"];
const BASS = ["G1", "C2", "E2", "A2", "E3"];
const VIOLIN = ["A3", "C4", "E4", "G4", "C5", "E5"];
const BASE = `${process.env.PUBLIC_URL || ""}/audio/`;
const rnd = (a, b) => a + Math.random() * (b - a);
const map = (keys, path) => Object.fromEntries(keys.map((k) => [k.replace("s", "#"), `${path}${k}.mp3`]));

function hallImpulse(ctx, seconds = 3.4) {
  const rate = ctx.sampleRate;
  const len = Math.floor(rate * seconds);
  const buf = ctx.createBuffer(2, len, rate);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    let lp = 0;
    for (let i = 0; i < len; i++) {
      const t = i / rate;
      const damp = Math.min(0.93, 0.2 + t * 0.28);
      lp = lp * damp + (Math.random() * 2 - 1) * (1 - damp);
      d[i] = t < 0.02 ? 0 : lp * Math.exp(-t * 2.1) * 1.8;
    }
    [0.021, 0.029, 0.043, 0.057, 0.071].forEach((e, k) => (d[Math.floor((e + ch * 0.003) * rate)] += 0.5 - k * 0.07));
  }
  return buf;
}

class SongPlayer {
  constructor(engine, slot, mood) {
    this.e = engine;
    this.T = engine.T;
    this.slot = slot;
    this.m = mood;
    this.song = SONGS[mood.song] || SONGS.major;
    this.order = engine.started ? LOOP : ORDER;
    this.si = 0;
    this.bi = 0;
    this.count = 0;
    this.cache = {};
  }

  start(t) {
    this.next = t;
    this.timer = setInterval(() => this.tick(), 60);
    this.tick();
  }

  stop() {
    clearInterval(this.timer);
  }

  tick() {
    const ahead = this.T.now() + 0.6;
    while (this.next < ahead) {
      const name = this.order[this.si];
      const sec = this.song[name];
      const base = (60 / this.m.bpm) * (name === "intro" ? 1.18 : PHRASE[this.bi % 4]);
      const beats = LILT.map((f) => f * base);
      this.e.section = name;
      this.playBar(name, sec[this.bi], this.next, beats);
      this.next += beats[0] + beats[1] + beats[2];
      if (++this.bi >= sec.length) {
        this.bi = 0;
        if (++this.si >= this.order.length) {
          this.order = LOOP;
          this.si = 0;
        }
      }
    }
  }

  n(note, extra = 0) {
    const k = `${note}:${extra}`;
    if (!(k in this.cache)) this.cache[k] = this.T.Frequency(note).transpose(this.m.key + extra).toNote();
    return this.cache[k];
  }

  hz(note, cents) {
    return this.T.Frequency(note).toFrequency() * Math.pow(2, cents / 1200);
  }

  hit(voice, notes, time, dur, vel, dyn) {
    if (!voice) return;
    voice.triggerAttackRelease(notes, Math.max(0.05, dur), Math.max(this.T.now() + 0.01, time), Math.min(1, vel * dyn * rnd(0.9, 1.08)));
  }

  playBar(secName, bar, t, beats) {
    const { m, slot } = this;
    const mix = SECTION_MIX[secName];
    const thin = perf.fps < 30;
    this.e.thin = thin;
    const at = (b) => {
      const k = Math.min(2, Math.floor(b));
      return t + beats.slice(0, k).reduce((a, c) => a + c, 0) + (b - k) * beats[k];
    };
    const end = at(3);
    const change = bar.second ? at(2) : end;
    const dyn = mix.dyn * (0.88 + 0.16 * Math.sin((Math.PI * ((this.count++ % 8) + 0.5)) / 8));
    const V = bar.voicing.map((x) => this.n(x));
    const root = this.n(bar.bass[0]);
    const lay = () => rnd(-0.012, 0.012);

    if (secName === "intro" || m.comp === "arp") {
      const arp = [...V, this.n(bar.voicing[0], 12), this.n(bar.voicing[1], 12)];
      for (let k = 0; k < 6; k++) this.hit(slot.piano, arp[k % arp.length], at(k * 0.5) + lay(), change - at(k * 0.5) + 0.2, 0.17 - k * 0.008, dyn);
      if (secName === "intro" || m.bass === "pedal") this.hit(slot.piano, root, t, end - t + 0.3, 0.26, dyn);
    } else if (m.comp === "waltz") {
      this.hit(slot.piano, V, at(1) + lay(), change - at(1) + 0.1, 0.2, dyn);
      this.hit(slot.piano, bar.second ? bar.second.map((x) => this.n(x)) : V, at(2) + lay(), end - at(2) + 0.1, 0.15, dyn);
    } else {
      V.forEach((x, k) => this.hit(slot.piano, x, t + 0.035 * k, change - t + 0.15, 0.17, dyn));
      this.hit(slot.piano, this.n(bar.bass[0], -12), t, end - t + 0.3, 0.22, dyn);
      this.hit(slot.piano, bar.second ? bar.second.map((x) => this.n(x)) : V, at(2) + lay(), end - at(2) + 0.1, 0.14, dyn);
    }

    if (secName !== "intro") {
      const evs = m.melody === "sparse" ? bar.events.filter((_, k) => k === 0 || (k === 1 && this.count % 2)) : bar.events;
      evs.forEach((e, k) => {
        const when = at(e.beat) + rnd(0, 0.024);
        const note = this.n(e.note, m.melody === "sparse" ? m.octave : 0);
        const vel = (k === 0 ? 0.5 : 0.42) * (e.dur >= 1.5 ? 1.05 : 0.97) * (m.melody === "sparse" ? 0.7 : 1);
        const dur = Math.max(e.dur * beats[0], change - when) + 0.1;
        this.hit(slot.piano, note, when, dur, vel, dyn);
        if (m.melody === "double") this.hit(slot.piano, this.n(e.note, -12), when + 0.008, dur, vel * 0.5, dyn);
      });
    }

    if (mix.bass > 0) {
      if (m.bass === "walk" && !thin) bar.bass.forEach((b, k) => this.hit(slot.bass, this.n(b), at(k) + lay(), beats[k] * 0.92, (k === 0 ? 0.55 : 0.4) * mix.bass, dyn));
      else this.hit(slot.bass, root, t, end - t, 0.34 * mix.bass, dyn);
    }

    const sv = m.strings * mix.strings;
    if (sv > 0.01) {
      [this.n(bar.bass[0], 12), ...V.slice(0, 3)].forEach((x) => {
        this.hit(slot.cello, this.hz(x, -6), t, end - t + 0.5, sv, dyn);
        if (!thin) this.hit(slot.cello, this.hz(x, 7), t + 0.04, end - t + 0.5, sv * 0.7, dyn);
      });
    }
    const vv = m.violin * mix.violin;
    if (slot.violin && !thin && vv > 0.01) {
      V.slice(-2).forEach((x) => {
        const hi = this.T.Frequency(x).transpose(12).toNote();
        this.hit(slot.violin, this.hz(hi, -5), t + 0.03, end - t + 0.6, vv, dyn);
        this.hit(slot.violin, this.hz(hi, 6), t + 0.08, end - t + 0.6, vv * 0.7, dyn);
      });
    }
  }
}

class Orchestra {
  T = null;
  loaded = false;
  violinReady = false;
  started = false;
  current = null;
  want = null;
  slots = [];
  active = -1;
  section = null;
  thin = false;

  prefetch() {
    [...PIANO.map((k) => `piano/${k}.mp3`), ...CELLO.map((k) => `strings/cello_${k}.mp3`), ...BASS.map((k) => `bass/cb_${k}.mp3`), ...VIOLIN.map((k) => `strings/violin_${k}.mp3`)].forEach(
      (u) => fetch(BASE + u, { cache: "force-cache" }).catch(() => {}),
    );
  }

  async init(ctx, destination) {
    if (this.initing || !ctx) return;
    this.initing = true;
    try {
      const T = await import("tone");
      T.setContext(ctx);
      const mixBus = new T.Gain(1);
      const out = new T.Gain(0.85);
      out.connect(destination);
      mixBus.connect(new T.Gain(0.72).connect(out));
      const hall = new T.Convolver(new T.ToneAudioBuffer(hallImpulse(ctx)));
      mixBus.connect(hall);
      hall.connect(new T.Gain(0.45).connect(out));
      const bufs = (urls) => new Promise((res) => {
        const b = new T.ToneAudioBuffers({ urls, baseUrl: BASE, onload: () => res(b) });
      });
      const [piano, cello, bass] = await Promise.all([bufs(map(PIANO, "piano/")), bufs(map(CELLO, "strings/cello_")), bufs(map(BASS, "bass/cb_"))]);
      const sampler = (b, keys, opts) => new T.Sampler({ urls: Object.fromEntries(keys.map((k) => [k.replace("s", "#"), b.get(k.replace("s", "#"))])), ...opts });
      this.slots = [0, 1].map(() => {
        const gain = new T.Gain(0).connect(mixBus);
        return {
          gain,
          piano: sampler(piano, PIANO, { release: 1.4 }).connect(new T.Volume(-3).connect(gain)),
          bass: sampler(bass, BASS, { attack: 0.02, release: 0.5 }).connect(new T.Filter(900, "lowpass").connect(new T.Volume(-5).connect(gain))),
          cello: sampler(cello, CELLO, { attack: 0.9, release: 2.6 }).connect(new T.Filter(1700, "lowpass").connect(new T.Panner(-0.25).connect(new T.Volume(-13).connect(gain)))),
          violin: null,
          player: null,
        };
      });
      this.T = T;
      this.loaded = true;
      if (this.want) this.play(...this.want);
      const violin = await bufs(map(VIOLIN, "strings/violin_"));
      this.slots.forEach((s) => {
        s.violin = sampler(violin, VIOLIN, { attack: 1.2, release: 2.8 }).connect(new T.Filter(2600, "lowpass").connect(new T.Panner(0.3).connect(new T.Volume(-16).connect(s.gain))));
      });
      this.violinReady = true;
    } catch {
      this.loaded = false;
    }
  }

  play(name, mood = HALL_MOOD, level = 1) {
    this.want = [name, mood, level];
    if (!this.loaded || this.current === name) return;
    try {
      const now = this.T.now();
      if (this.active >= 0) {
        const old = this.slots[this.active];
        old.player?.stop();
        old.gain.gain.cancelScheduledValues(now);
        old.gain.gain.setTargetAtTime(0, now, 1.0);
      }
      const idx = this.active === 0 ? 1 : 0;
      const s = this.slots[idx];
      s.player?.stop();
      s.gain.gain.cancelScheduledValues(now);
      s.gain.gain.setTargetAtTime(level, now, level > 1 ? 1.8 : 0.9);
      s.player = new SongPlayer(this, s, { ...HALL_MOOD, ...mood });
      s.player.start(now + 0.05);
      this.started = true;
      this.active = idx;
      this.current = name;
      this.mood = { ...HALL_MOOD, ...mood };
    } catch {
      /* music is optional */
    }
  }

  rest(name) {
    this.want = null;
    this.current = `rest:${name}`;
    if (!this.loaded || this.active < 0) return;
    try {
      const now = this.T.now();
      const old = this.slots[this.active];
      old.player?.stop();
      old.gain.gain.cancelScheduledValues(now);
      old.gain.gain.setTargetAtTime(0, now, 1.0);
      this.active = -1;
    } catch {
      /* optional */
    }
  }

  chime() {
    if (!this.loaded || this.active < 0) return;
    try {
      this.slots[this.active].piano.triggerAttackRelease(CHIME[Math.floor(Math.random() * CHIME.length)], 2, this.T.now(), 0.3);
    } catch {
      /* optional */
    }
  }

  state() {
    const on = this.loaded;
    return {
      loaded: on,
      variation: this.current,
      mood: this.mood || null,
      section: this.section,
      thin: this.thin,
      crossfadeSeconds: 3,
      layers: { piano: on, bass: on && !!this.mood && this.mood.bass !== "none", strings: on, violin: this.violinReady && !this.thin, reverb: on },
    };
  }
}

export const music = new Orchestra();
